import { graphQLClient } from '@api/client';
import {
  PaymentStatusEnum,
  Treatment,
  useTreatmentHistoryDetailQuery
} from '@api/GoodocAPI';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import {
  TREATMENT_HISTORY_DEETAIL_BACK_BUTTON,
  TREATMENT_HISTORY_DETAIL_PRESCRIPTION_BUTTON,
  TREATMENT_HISTORY_DETAIL_RETRY_PAYMENT_BUTTON,
  TREATMENT_HISTORY_DETAIL_TREATMENT_PAYMENT_BUTTON
} from '@pages/TreatmentHistoryDetail/focusKey';
import {
  generatePaymentPath,
  generatePrescriptionPath,
  generateRetryPaymentPath,
  TargetPayment
} from '@router/path';
import constate from 'constate';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPrescriptionState, getTreatmentPrice } from '@utils/treatmentUtil';

const useTreatmentHistoryDetail = () => {
  const navigate = useNavigate();
  const { setFocus } = useFocusable();
  const { treatmentId = '' } = useParams();
  const { data: treatmentHistoryDetailResponse } = useTreatmentHistoryDetailQuery(
    graphQLClient,
    { treatmentId }
  );

  const { treatmentV3: treatment } = treatmentHistoryDetailResponse ?? {};
  const { prescription } = treatment ?? {};

  const treatmentPrice = useMemo(() => {
    return getTreatmentPrice(treatment as Treatment);
  }, [treatment]);

  const prescriptionState = useMemo(() => {
    return getPrescriptionState(treatment as Treatment);
  }, [treatment]);

  // PharmacyStatus
  const isPharmacyStatusVisible = useMemo(() => {
    return (
      (treatment?.paymentStatus === PaymentStatusEnum.PaymentCompleted ||
        treatment?.paymentStatus === PaymentStatusEnum.RefundFailed) &&
      prescription?.deliveryMethod &&
      prescription?.delivery
    );
  }, [treatment?.paymentStatus, prescription?.delivery, prescription?.deliveryMethod]);

  const makingStatusText = useMemo(() => {
    if (prescription) {
      const { deliveryMethod, delivery } = prescription;
      switch (deliveryMethod) {
        case 'parcel':
          switch (delivery?.parcelStatus) {
            case 'waiting':
              return '대기 중';
            case 'makingStart':
              return '조제 중';
            case 'deliveryStart':
              return '조제 완료';
          }
          break;
        case 'directdelivery':
          switch (delivery?.directDeliveryStatus) {
            case 'waiting':
              return '대기 중';
            case 'makingStart':
              return '조제 중';
            case 'pickupRequest':
            case 'pickupComplete':
              return '조제 완료';
          }
          break;
      }
    }
  }, [prescription]);

  const makingCompleteAt = useMemo(() => {
    if (prescription) {
      const { deliveryMethod, delivery } = prescription;
      switch (deliveryMethod) {
        case 'parcel':
          return delivery?.parcelDeliveryStartAt;
        case 'directdelivery':
          return delivery?.directPickupRequestAt;
      }
    }
  }, [prescription]);

  const prescriptionPayments = useMemo(
    () =>
      prescription?.prescriptionPayments?.sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt)
      ) ?? [],
    [prescription?.prescriptionPayments]
  );

  const lastPrescriptionPayment = useMemo(() => {
    return prescriptionPayments.length > 0
      ? prescriptionPayments[prescriptionPayments.length - 1]
      : null;
  }, [prescriptionPayments]);

  const prescriptionPrice = useMemo(() => {
    if (lastPrescriptionPayment) {
      const price = prescriptionPayments.reduce((previousValue, currentValue) => {
        switch (currentValue.paymentStatus) {
          case 'complete':
            return previousValue + currentValue.price;
          case 'cancel':
            return previousValue - currentValue.price;
          case 'fail':
          default:
            return previousValue;
        }
      }, 0);
      const failedPrice =
        lastPrescriptionPayment && lastPrescriptionPayment.paymentStatus === 'fail'
          ? lastPrescriptionPayment.price
          : 0;
      return price + failedPrice;
    }
  }, [lastPrescriptionPayment, prescriptionPayments]);

  // DeliveryStatus
  const isDeliveryStatusVisible = useMemo(() => {
    if (lastPrescriptionPayment?.paymentStatus === 'complete' && prescription) {
      const { deliveryMethod, delivery } = prescription;
      switch (deliveryMethod) {
        case 'parcel':
          switch (delivery?.parcelStatus) {
            case 'deliveryStart':
              return true;
          }
          break;
        case 'directdelivery':
          switch (delivery?.directDeliveryStatus) {
            case 'pickupRequest':
            case 'pickupComplete':
              return true;
          }
          break;
      }
    }
    return false;
  }, [lastPrescriptionPayment?.paymentStatus, prescription]);

  const deliveryStatusText = useMemo(() => {
    if (prescription) {
      const { deliveryMethod, delivery } = prescription;
      switch (deliveryMethod) {
        case 'parcel':
          switch (delivery?.parcelStatus) {
            case 'deliveryStart':
              return '배송 중';
          }
          break;
        case 'directdelivery':
          switch (delivery?.directDeliveryStatus) {
            case 'pickupRequest':
              return '배송 요청';
            case 'pickupComplete':
              return '배송 중';
          }
          break;
      }
    }
  }, [prescription]);

  const deliveryPayments = useMemo(
    () =>
      prescription?.delivery?.deliveryPayments?.sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt)
      ) ?? [],
    [prescription?.delivery?.deliveryPayments]
  );

  const lastDeliveryPayment = useMemo(() => {
    return deliveryPayments.length > 0
      ? deliveryPayments[deliveryPayments.length - 1]
      : null;
  }, [deliveryPayments]);

  const deliveryPrice = useMemo(() => {
    if (lastDeliveryPayment) {
      const price = deliveryPayments.reduce((previousValue, currentValue) => {
        switch (currentValue.paymentStatus) {
          case 'complete':
            return previousValue + currentValue.price;
          case 'cancel':
            return previousValue - currentValue.price;
          case 'fail':
          default:
            return previousValue;
        }
      }, 0);
      const failedPrice =
        lastDeliveryPayment && lastDeliveryPayment.paymentStatus === 'fail'
          ? lastDeliveryPayment.price
          : 0;
      return price + failedPrice;
    }
  }, [deliveryPayments, lastDeliveryPayment]);

  const isRetryPayment = useMemo(
    () =>
    treatment?.paymentStatus === PaymentStatusEnum.PaymentFailed ||
      lastPrescriptionPayment?.paymentStatus === 'fail' ||
      lastDeliveryPayment?.paymentStatus === 'fail',
    [
      lastDeliveryPayment?.paymentStatus,
      lastPrescriptionPayment?.paymentStatus,
      treatment?.paymentStatus
    ]
  );

  const setButtonFocus = useCallback(() => {
    if (prescriptionState === 'needPayment') {
      return setFocus(TREATMENT_HISTORY_DETAIL_TREATMENT_PAYMENT_BUTTON);
    }
    if (isRetryPayment) {
      setFocus(TREATMENT_HISTORY_DETAIL_RETRY_PAYMENT_BUTTON);
    } else if (prescriptionState === 'hasPrescription') {
      setFocus(TREATMENT_HISTORY_DETAIL_PRESCRIPTION_BUTTON);
    } else {
      setFocus(TREATMENT_HISTORY_DEETAIL_BACK_BUTTON);
    }
  }, [prescriptionState, isRetryPayment, setFocus]);

  const goTreatmentPayment = useCallback(() => {
    navigate(generatePaymentPath(TargetPayment.treatment, treatmentId));
  }, [navigate, treatmentId]);

  const goPrescription = useCallback(() => {
    navigate(generatePrescriptionPath(treatmentId));
  }, [navigate, treatmentId]);

  const goRetryPayment = useCallback(() => {
    navigate(generateRetryPaymentPath(treatmentId));
  }, [navigate, treatmentId]);

  return {
    treatment,
    isRetryPayment,
    treatmentPrice,
    prescriptionState,
    isPharmacyStatusVisible,
    makingStatusText,
    makingCompleteAt,
    prescriptionPrice,
    lastPrescriptionPayment,
    isDeliveryStatusVisible,
    deliveryStatusText,
    deliveryPrice,
    lastDeliveryPayment,
    setButtonFocus,
    goTreatmentPayment,
    goPrescription,
    goRetryPayment
  };
};

export const [TreatmentHistoryDetailProvider, useTreatmentHistoryDetailContext] =
  constate(useTreatmentHistoryDetail);
